import React, { useState } from 'react';
import { Pill, FlaskConical, Syringe, Send, ImageIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useEncounter } from '@/contexts/EncounterContext';
import { cn } from '@/lib/utils';
import VisitRxPanel from './VisitRxPanel';
import VisitOrdersPanel from './VisitOrdersPanel';
import PatientImmunizationsTab from '../patient/PatientImmunizationsTab';
import { type CreateLabOrderRequest } from '@/services/labService';

type PlanTab = 'rx' | 'labs' | 'imaging' | 'immun' | 'referral';

interface PlanPanelProps {
  practitionerId: string;
  patientDetails: CreateLabOrderRequest['patientDetails'];
  patientAddress: CreateLabOrderRequest['patientAddress'];
}

const TABS: Array<{ id: PlanTab; label: string; icon: React.ElementType; disabled?: boolean }> = [
  { id: 'rx', label: 'Prescriptions', icon: Pill },
  { id: 'labs', label: 'Lab orders', icon: FlaskConical },
  { id: 'imaging', label: 'Imaging', icon: ImageIcon, disabled: true },
  { id: 'immun', label: 'Immunizations', icon: Syringe },
  { id: 'referral', label: 'Referrals', icon: Send, disabled: true },
];

/**
 * Phase 4 — combined "Plan" tab. Houses every "what are we doing today?"
 * action behind a single set of chips so the practitioner doesn't have to
 * leave the tab when switching from prescribing to ordering labs.
 *
 * Each chip routes to the existing dedicated panel — the chips are the
 * only new UI; the panels themselves are unchanged.
 */
const PlanPanel: React.FC<PlanPanelProps> = ({
  practitionerId, patientDetails, patientAddress,
}) => {
  const [active, setActive] = useState<PlanTab>('rx');
  const { patientId } = useEncounter();

  return (
    <div className='space-y-4'>
      <Card>
        <CardContent className='py-3'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='mr-1 text-xs font-semibold uppercase tracking-wide text-slate-500'>
              What are you ordering today?
            </span>
            {TABS.map(t => {
              const Icon = t.icon;
              const isActive = active === t.id;
              return (
                <Button
                  key={t.id}
                  type='button'
                  variant={isActive ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => !t.disabled && setActive(t.id)}
                  disabled={t.disabled}
                  className={cn(
                    'gap-1',
                    isActive && 'bg-brand-teal hover:bg-brand-teal/90',
                  )}
                  title={t.disabled ? 'Coming soon' : undefined}
                >
                  <Icon className='h-3.5 w-3.5' />
                  {t.label}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {active === 'rx' && <VisitRxPanel practitionerId={practitionerId} />}
      {active === 'labs' && (
        <VisitOrdersPanel
          patientDetails={patientDetails}
          patientAddress={patientAddress}
        />
      )}
      {active === 'immun' && <PatientImmunizationsTab patientId={patientId} />}
    </div>
  );
};

export default PlanPanel;
